import { useTranslation } from 'react-i18next';
import { Switch } from '@/lib/ui/Switch';
import { Tag } from '@/lib/ui/Tag';
import { cn } from '@/lib/ui/cn';
import { arCompleteness } from '@/lib/i18n/content';

/**
 * Arabic-completeness control (contract §4.3). The publish gate (`locale_incomplete`) keys off
 * `doc.ar_complete`, so an editor needs (a) a live view of how much Arabic is still missing and
 * (b) an explicit "Arabic is complete" switch. We compute coverage from the actual bilingual
 * leaves of the entity, so the meter is honest even before the flag is flipped.
 *
 * `coverageOf` is the value to scan for `{en,ar}` leaves (title + doc, or the whole family).
 */
export function ArabicCompleteControl({
  coverageOf,
  checked,
  onChange,
  disabled,
}: {
  coverageOf: unknown;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { total, missing } = arCompleteness(coverageOf);
  const translated = total - missing;
  const pct = total === 0 ? 100 : Math.round((translated / total) * 100);
  const done = missing === 0 && total > 0;

  return (
    <div className="flex flex-col gap-sm rounded-md border-[0.5px] border-hairline bg-canvas-sand p-md">
      <div className="flex items-center justify-between gap-sm">
        <span className="eyebrow text-ink-mute">{t('governance.arCoverage')}</span>
        {total === 0 ? (
          <Tag variant="neutral">{t('governance.noTranslatableFields')}</Tag>
        ) : (
          <Tag variant={done ? 'teal' : 'amber'}>
            {t('governance.coverageCount', { translated, total })}
          </Tag>
        )}
      </div>
      <div className="h-[6px] overflow-hidden rounded-pill bg-canvas-deep-sand">
        <div
          className={cn('h-full rounded-pill transition-[width]', done ? 'bg-signal-teal' : 'bg-signal-amber')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <Switch
        label={t('governance.arabicComplete')}
        hint={
          missing === 0
            ? t('governance.arabicCompleteHintReady')
            : t('governance.arabicCompleteHintPending', { count: missing })
        }
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
      />
      {!checked && (
        <p className="text-small text-ink-mute">{t('governance.arabicCompleteGate')}</p>
      )}
    </div>
  );
}
